import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import DictationInput from '../DictationInput';
import { toast } from 'sonner';
import { LogOut, Plus, CheckCircle, Circle, MapPin } from 'lucide-react';

export default function MoveoutPlanner({ api, residentId, userId, houseId, isStaff }) {
  const [plans, setPlans] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    target_move_out_date: '',
    housing_search_status: 'not_started',
    destination_type: '',
    notes: ''
  });

  const load = useCallback(() => {
    api.get(`/housing/moveout-plans?resident_id=${residentId}`)
      .then(r => setPlans(r.data || []))
      .catch(() => {});
  }, [api, residentId]);

  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    try {
      await api.post('/housing/moveout-plans', {
        resident_id: residentId,
        user_id: userId,
        house_id: houseId,
        ...form
      });
      toast.success('Move-out plan created!');
      setAddOpen(false);
      load();
    } catch {
      toast.error('Failed to create plan');
    }
  };

  const toggleChecklist = async (planId, checklist, index) => {
    const updated = checklist.map((item, i) => i === index ? { ...item, completed: !item.completed } : item);
    try {
      await api.put(`/housing/moveout-plans/${planId}`, { checklist: updated });
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const updateStatus = async (planId, status) => {
    try {
      await api.put(`/housing/moveout-plans/${planId}`, { housing_search_status: status });
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const activePlan = plans.find(p => p.status === 'active');

  return (
    <Card className="border-0 shadow-sm rounded-xl" data-testid="moveout-planner">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <LogOut size={14} /> Move-Out Plan
        </CardTitle>
        {isStaff && !activePlan && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" data-testid="create-moveout-btn">
                <Plus size={12} /> Create Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Create Move-Out Plan</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label>Target Move-Out Date</Label>
                  <Input type="date" value={form.target_move_out_date} onChange={e => setForm({...form, target_move_out_date: e.target.value})} data-testid="moveout-date-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Housing Search Status</Label>
                  <Select value={form.housing_search_status} onValueChange={v => setForm({...form, housing_search_status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="searching">Searching</SelectItem>
                      <SelectItem value="applied">Applied</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="secured">Secured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Destination Type</Label>
                  <Select value={form.destination_type || 'unknown'} onValueChange={v => setForm({...form, destination_type: v === 'unknown' ? '' : v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Not Yet Determined</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="sober_living">Sober Living</SelectItem>
                      <SelectItem value="family">Family / Friends</SelectItem>
                      <SelectItem value="shelter">Shelter</SelectItem>
                      <SelectItem value="other_program">Other Program</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <DictationInput value={form.notes} onChange={val => setForm({...form, notes: val})} rows={2} placeholder="Any notes about the transition..." showCleanup={false} />
                </div>
                <Button onClick={createPlan} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-moveout-plan">Create Plan</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {activePlan ? (
          <div className="space-y-3">
            {/* Plan Summary */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div>
                <p className="text-xs text-muted-foreground">Target Date</p>
                <p className="text-sm font-medium">{activePlan.target_move_out_date || 'Not set'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Housing Search</p>
                <Badge className={
                  activePlan.housing_search_status === 'secured' ? 'bg-emerald-50 text-emerald-700 border-0'
                  : activePlan.housing_search_status === 'approved' ? 'bg-blue-50 text-blue-700 border-0'
                  : 'bg-amber-50 text-amber-700 border-0'
                }>{activePlan.housing_search_status?.replace(/_/g, ' ')}</Badge>
              </div>
            </div>

            {activePlan.destination_type && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin size={12} /> Destination: {activePlan.destination_type.replace(/_/g, ' ')}
                {activePlan.destination_address && <span>- {activePlan.destination_address}</span>}
              </div>
            )}

            {/* Search Status Buttons */}
            {isStaff && (
              <div className="flex gap-1.5 flex-wrap">
                {['not_started', 'searching', 'applied', 'approved', 'secured'].map(s => (
                  <Button key={s} size="sm" variant={activePlan.housing_search_status === s ? 'default' : 'outline'}
                    className={`rounded-full text-[10px] h-7 ${activePlan.housing_search_status === s ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                    onClick={() => updateStatus(activePlan.id, s)}
                  >{s.replace(/_/g, ' ')}</Button>
                ))}
              </div>
            )}

            {/* Checklist */}
            {activePlan.checklist?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transition Checklist</p>
                {activePlan.checklist.map((item, i) => (
                  <button
                    key={`check-${i}`}
                    type="button"
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                      item.completed ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                    onClick={() => isStaff && toggleChecklist(activePlan.id, activePlan.checklist, i)}
                    disabled={!isStaff}
                    data-testid={`moveout-check-${i}`}
                  >
                    {item.completed ? <CheckCircle size={14} className="text-emerald-600 shrink-0" /> : <Circle size={14} className="text-slate-400 shrink-0" />}
                    <span className={item.completed ? 'line-through' : ''}>{item.item}</span>
                  </button>
                ))}
                <p className="text-[10px] text-muted-foreground text-center mt-1">
                  {activePlan.checklist.filter(c => c.completed).length} / {activePlan.checklist.length} complete
                </p>
              </div>
            )}

            {activePlan.notes && (
              <div className="p-2 rounded-lg bg-slate-50">
                <p className="text-[10px] text-muted-foreground mb-0.5">Notes</p>
                <p className="text-xs text-slate-700">{activePlan.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-3">
            {isStaff ? 'No move-out plan yet - create one when the resident is ready to transition' : 'No move-out plan created yet'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
